const commerceApiUrl = String(
  import.meta.env.VITE_COMMERCE_API_URL || "http://127.0.0.1:8791"
).replace(/\/$/, "");

async function getPublicJson(url, { signal, errorMessage }) {
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });

  let result;
  try {
    result = await response.json();
  } catch {
    throw new Error("A loja retornou uma resposta inválida.");
  }

  if (!response.ok) {
    const error = new Error(result?.error || errorMessage);
    error.status = response.status;
    throw error;
  }

  return result?.data;
}

export async function resolveStorefrontHost(host, { signal } = {}) {
  const url = new URL(`${commerceApiUrl}/v1/storefront/resolve`);
  url.searchParams.set("host", host);
  return getPublicJson(url, {
    signal,
    errorMessage: "Não encontramos uma loja neste domínio.",
  });
}

export async function fetchCatalog(
  storeSlug,
  { page = 1, limit = 12, filters = {}, signal, preview = false } = {}
) {
  const path = `/v1/storefront/stores/${encodeURIComponent(
    storeSlug
  )}/products`;
  const url = new URL(`${commerceApiUrl}${path}`);
  url.searchParams.set("page", String(page));
  url.searchParams.set("limit", String(limit));
  if (preview) url.searchParams.set("preview", "1");
  if (filters.search) url.searchParams.set("search", filters.search);
  if (filters.category) url.searchParams.set("category", filters.category);
  if (filters.tag) url.searchParams.set("tag", filters.tag);
  if (filters.minPrice !== "" && filters.minPrice != null) {
    url.searchParams.set("minPrice", String(filters.minPrice));
  }
  if (filters.maxPrice !== "" && filters.maxPrice != null) {
    url.searchParams.set("maxPrice", String(filters.maxPrice));
  }

  return getPublicJson(url, {
    signal,
    errorMessage: "Não foi possível carregar os produtos.",
  });
}

export function fetchStorefrontCategories(storeSlug, { signal } = {}) {
  const path = `/v1/storefront/stores/${encodeURIComponent(
    storeSlug
  )}/categories`;
  return getPublicJson(`${commerceApiUrl}${path}`, {
    signal,
    errorMessage: "Não foi possível carregar as categorias.",
  });
}

const homePageCache = new Map();

export function fetchHomePage(storeSlug, { preview = false, pageId = "" } = {}) {
  const params = new URLSearchParams();
  if (preview) params.set("preview", "1");
  if (preview && pageId) params.set("page", pageId);
  const query = params.toString();
  const path = `/v1/storefront/stores/${encodeURIComponent(
    storeSlug
  )}/home-page${query ? `?${query}` : ""}`;
  const cacheKey = preview ? `${storeSlug}:preview:${pageId}` : storeSlug;
  const cached = homePageCache.get(cacheKey);
  if (cached) return cached;

  const request = getPublicJson(`${commerceApiUrl}${path}`, {
    errorMessage: "Não foi possível carregar a página inicial.",
  }).catch((error) => {
    homePageCache.delete(cacheKey);
    throw error;
  });
  homePageCache.set(cacheKey, request);
  window.setTimeout(() => homePageCache.delete(cacheKey), preview ? 0 : 30000);
  return request;
}

export function fetchContentPage(storeSlug, pageSlug, { preview = false, signal } = {}) {
  const path = `/v1/storefront/stores/${encodeURIComponent(
    storeSlug
  )}/pages/${encodeURIComponent(pageSlug)}${preview ? "?preview=1" : ""}`;
  return getPublicJson(`${commerceApiUrl}${path}`, {
    signal,
    errorMessage: "Não foi possível carregar a página.",
  });
}

export function fetchProduct(storeSlug, productHandle, { signal } = {}) {
  const path = `/v1/storefront/stores/${encodeURIComponent(
    storeSlug
  )}/products/${encodeURIComponent(productHandle)}`;
  return getPublicJson(`${commerceApiUrl}${path}`, {
    signal,
    errorMessage: "Não foi possível carregar o produto.",
  });
}

export function fetchPaidOrder(storeSlug, orderId, { signal } = {}) {
  const path = `/v1/storefront/stores/${encodeURIComponent(
    storeSlug
  )}/orders/${encodeURIComponent(orderId)}`;
  return getPublicJson(`${commerceApiUrl}${path}`, {
    signal,
    errorMessage: "Não foi possível carregar o pedido.",
  });
}

export async function prepareCheckout(
  storeSlug,
  items,
  { idempotencyKey, signal } = {}
) {
  const path = `/v1/storefront/stores/${encodeURIComponent(
    storeSlug
  )}/checkout/prepare`;
  const response = await fetch(`${commerceApiUrl}${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify({
      items: items.map((item) => ({
        variantId: item.variantId,
        quantity: Number(item.quantity),
      })),
    }),
    signal,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(
      result?.details
        ? `${result.error || "Não foi possível iniciar o checkout."}: ${result.details}`
        : result?.error || "Não foi possível iniciar o checkout."
    );
    error.status = response.status;
    error.code = result?.code;
    throw error;
  }
  return result.data;
}

export async function validateCart(storeSlug, items, { signal } = {}) {
  const path = `/v1/storefront/stores/${encodeURIComponent(
    storeSlug
  )}/checkout/validate`;
  const response = await fetch(`${commerceApiUrl}${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      items: items.map((item) => ({
        variantId: item.variantId,
        quantity: Number(item.quantity),
      })),
    }),
    signal,
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(
      result?.error || "Não foi possível verificar a disponibilidade."
    );
    error.status = response.status;
    error.code = result?.code;
    throw error;
  }
  return result.data;
}
